// - - - - - - - - - -

// App Version: MM-DD-YYYY-BB (date + build number)
// Increment build number for each deployment on the same day
export const APP_VERSION = "06-26-2026-01";
// Time Machine #114 — Phases 4–6 ship to PROD with two complementary fixes
// and a per-user access gate.
//
// What's in this build (vs the previous live v05-07-2026-01):
//   1. Time Machine UI (Phases 4 + 4.5 + 5 + 6) — date+slider picker, sepia
//      banner, in-memory replay, event-log history modals (job + crew +
//      attribute-cell + customer-portal-audit), Diff Pair multi-grid view.
//   2. Per-user access gate — the "🕒 Time Travel" button is hidden unless
//      `Users.timeTravelAccess === true` (Compass-managed; default false on
//      every existing user). Lets us deploy to PROD with zero rollout impact
//      and progressively flip individual testers.
//   3. #116 z-axis layering fix — sticky header z-30 above bubbles + sticky-
//      left crew columns (visible vertical-scroll bleed-over, cosmetic).
//   4. #117 shared-installer cross-location parity — time-travel cells now
//      show jobs at OTHER locations assigned to shared installers, matching
//      live behavior. Lambda companion: api_mongoDB_queries
//      v05-09-2026-03-QRY01. PROD-verified parity (MEL 557/557).
//
// Tests: 785 passing (frontend) + 74 passing (dbtools).

// - - - - - - - - - -

// Axiom Wide Event Logging (https://axiom.co)
// Dataset and API token are stored in .env files (not here) for security
// See: .env, .env.development, .env.uat, .env.production
// Environment variables: REACT_APP_AXIOM_DATASET, REACT_APP_AXIOM_TOKEN

// - - - - - - - - - -

export const MILLISECONDS_PER_DAY = 1000 * 3600 * 24;

// Time Machine #114 — UI floor for the time-travel pickers.
//
// TWO floor concepts (different shapes for different pickers):
//
// 1. TIME_MACHINE_FLOOR_ISO — Phase 4 (legacy). The earliest moment-in-time
//    that asOf could be set to without hitting a Lambda floor refusal.
//    = latest of the 4 day-zero snapshot timestamps (Candidates 18:39:36).
//    The Phase 4 date+time picker (TimeMachinePicker.js) uses this as a
//    timestamp lower-bound. No longer rendered in App.js as of Phase 4.5,
//    but kept as an export so the deprecated component still compiles +
//    its tests still pass.
//
// 2. TIME_MACHINE_FLOOR_DATE — Phase 4.5+ (current). The earliest CALENDAR
//    DAY for which a full day-bundle (state at 00:00 UTC + every event) is
//    fully reconstructible. Phase 4.5's `getStateAndEventsForDate` Lambda
//    looks for a snapshot timestamp ≤ start-of-day-UTC. If the day-zero
//    snapshot is at 18:36 UTC May 7, then May 7's start-of-day (00:00 UTC)
//    is BEFORE the snapshot — refusal. The first day for which a snapshot
//    exists at-or-before its 00:00 UTC mark is May 8. Hence:
//        TIME_MACHINE_FLOOR_DATE = "2026-05-08".
//
// MAINTENANCE: both are static constants. As more snapshots accumulate from
// the weekly Sunday cron (Phase 2), the floor never moves backward — it can
// only move backward if older snapshots are added (which we don't currently
// plan to do — there's no source data before 2026-05-07). The floor would
// move FORWARD only if older snapshots were pruned (also not planned).
export const TIME_MACHINE_FLOOR_ISO = "2026-05-07T18:39:36.042Z";
export const TIME_MACHINE_FLOOR_DATE = "2026-05-08";

// - - - - - - - - - -

// 2025-02-07 - project search reveals this apiKey is never used....
// export const MONGODB_API_KEY = "oKpNMep7LK1eqAIk1szb66GByIxPg8dk2sg6sxaY";
export const REALM_APP_ID = "scheduler-vcuhr"; // e.g. myapp-abcde
export const MONGODB_DATASOURCE_RW = "mongodb-atlas";
export const MONGODB_DATASOURCE_TS = "Scheduling";

// - - - - - - - - - -

export const MONGODB_AWSGW_APIKEY = "oKpNMep7LK1eqAIk1szb66GByIxPg8dk2sg6sxaY";
export const MONGODB_AWSGW_QUERIES =
  "https://ew2cvy1fqc.execute-api.us-east-1.amazonaws.com/v1/api_mongoDB_queries";
export const MONGODB_AWSGW_MUTATIONS =
  "https://ew2cvy1fqc.execute-api.us-east-1.amazonaws.com/v1/api_mongoDB_mutations";

// - - - - - - - - - -

// moved to ".env.xxx" and is dynamically loading via Environment Variables
// export const MONGODB_DATABASE_DEV = "UAT";
// export const MONGODB_DATABASE_PROD = "Warehouses";

export const MONGODB_COLLECTION_ACTIVEQUEUE = "ActiveQueue";
export const MONGODB_COLLECTION_CANDIDATES = "Candidates";
export const MONGODB_COLLECTION_CREWS = "Crews";
export const MONGODB_COLLECTION_ATTRIBUTES = "Attributes";
export const MONGODB_COLLECTION_USERS = "Users";
export const MONGODB_COLLECTION_OFFICES = "Offices";
export const MONGODB_COLLECTION_STORES = "Stores";
export const MONGODB_COLLECTION_LABORCATALOG = "LaborCatalog";
export const MONGODB_COLLECTION_LOGS = "Logs";

// - - - - - - - - - -

export const MONGODB_POLLING_INTERVAL_PROJECTS = 5; // seconds between polls
export const MONGODB_POLLING_INTERVAL_ATTRIBUTES = 10; // seconds between polls (attributes query takes 3-5 seconds)
export const MONGODB_POLLING_LOG_BATCH_INTERVAL = 5; // minutes - how often to send batched polling logs (set to 1 for testing, change to 5 for production)
export const MONGODB_POLLING_IDLE_TIMEOUT = 10; // minutes - pause polling after this many minutes of no user activity

// - - - - - - - - - -

export const MONGODB_MAXLIMIT_USERS = 1000;
export const MONGODB_MAXLIMIT_PROJECTSBYCOMPANY = 10000;
export const MONGODB_MAXLIMIT_CANDIDATESBYCOMPANY = 1000;
export const MONGODB_MAXLIMIT_CANDIDATESBYPAYROLLNOLIST = 1000;
export const MONGODB_MAXLIMIT_PROJECTSBYPAYROLLNOLIST = 1000;
export const MONGODB_MAXLIMIT_ATTRIBUTESBYLOCATION = 100000;
export const MONGODB_MAXLIMIT_LOGS = 10000;

// - - - - - - - - - -

// Attribute date cutoff - attributes older than this are filtered out from queries
// This reduces query payload size for locations with large historical data (e.g., RVS)
// Default/availability attributes (date === "") are always included regardless of this setting
export const MONGODB_ATTRIBUTES_DAYS_PAST = 14; // days - 2 weeks of historical data

// - - - - - - - - - -

export const FILTERS_SURFACE_CARPET = 0;
export const FILTERS_SURFACE_VINYL = 1;
export const FILTERS_SURFACE_PLANK = 2;
export const FILTERS_SURFACE_TILE = 3;
export const FILTERS_SURFACE_WOOD = 4;
export const FILTERS_SURFACE_BACKSPLASH = 5;
export const FILTERS_SURFACE_UNKNOWN = 6;
export const FILTERS_SURFACE_DETAIL = 7;
export const FILTERS_SURFACE_INTERIORDOOR = 8;
export const FILTERS_SURFACE_EXTERIORDOOR = 9;
export const FILTERS_SURFACE_STORMDOOR = 10;
export const FILTERS_SURFACE_WINDOW = 11;

// - - - - - - - - - -

export const FILTERS_CLASSIFICATION_DETAIL = 0;
export const FILTERS_CLASSIFICATION_JOB = 1;
export const FILTERS_CLASSIFICATION_RELATED_PO = 2;
export const FILTERS_CLASSIFICATION_WORKORDER_EXPENSE = 3;
export const FILTERS_CLASSIFICATION_REPAIR_ORDER = 4;
export const FILTERS_CLASSIFICATION_PAID_IN_ERROR = 5;
export const FILTERS_CLASSIFICATION_OTHER = 6;

// - - - - - - - - - -

export const FILTERS_CREW_DETAILER = 0;
export const FILTERS_CREW_INSTALLER = 1;
export const FILTERS_CREW_HELPER = 2;
export const FILTERS_CREW_EMPLOYEE = 3;

// - - - - - - - - - -

export const FILTERS_CREW_SURFACE_SOFT = 0;
export const FILTERS_CREW_SURFACE_HARD = 1;

// - - - - - - - - - -

export const FILTERS_SURFACE_ARRAY = [
  { name: "Carpet" },
  { name: "Vinyl" },
  { name: "Plank" },
  { name: "Tile" },
  { name: "Wood" },
  { name: "Backsplash" },
  { name: "Internal" },
  { name: "External" },
  { name: "Storm" },
  { name: "Window" },
  { name: "UNK" },
];
export const FILTERS_CLASSIFICATION_ARRAY = [
  { name: "Detail" },
  { name: "Job" },
  { name: "Related PO" },
  { name: "Workorder/Expense" },
  { name: "Repair Order" },
  { name: "Paid In Error" },
  { name: "Other" },
];
export const FILTERS_CREW_ARRAY = [
  { name: "Detailer" },
  { name: "Installer" },
  { name: "Helper" },
  { name: "Employee" },
];
export const FILTERS_CREW_SURFACE_ARRAY = [
  { name: "Soft Surface" },
  { name: "Hard Surface" },
];

// - - - - - - - - - -

// these are in the same order and matching Recruiter Capacities tab
export const CAPACITIES_SURFACE_ARRAY = [
  { name: "Carpet" },
  { name: "VinylPlank" },
  { name: "VinylSheet" },
  { name: "Tile" },
  { name: "Backsplash" },
  { name: "Floating" },
  { name: "GlueDown" },
  { name: "NailDown" },
  // #146 (2026-05-20) — per-installer stops caps. Read-only. Positioned
  // between NailDown and Storm per Brandon's 2026-05-20 request (was at
  // end of array in v05-20-2026-02; relocated in v05-20-2026-03).
  { name: "Stops (Jobs)" },
  { name: "Stops (Repairs)" },
  { name: "Storm" },
  { name: "External" },
  { name: "Internal" },
  { name: "Window" },
];

// - - - - - - - - - -

export const JOB_FURNITURE_SKULIST = [
  503239, 503276, 503273, 503252, 503266, 900001, 900008, 900017, 900022,
  900036, 1101360, 1101361, 1101362, 1101363, 253313, 1236788,
];

// - - - - - - - - - -

// #140 — Steps SKUs for capacity matching. Each matched line item contributes
// its `KI_AQ » InvoiceLineItems::qty` to the per-job step count, which is
// then matched against the installer's `capacity_carpet_<once|twice>_steps`
// fields in the recommender. Authoritative list delivered by Brandon
// 2026-05-19.
export const JOB_STEPS_SKULIST = [
  503233, 1236485, 1236787, 167763,
];

// - - - - - - - - - -

// #173 — Hard-surface capacity SKU lists. AUTHORITATIVE values delivered by
// Brandon 2026-06-02 (issue #173 comments). A hard-surface job is classified
// into a TYPE by which of these MAIN lists its InvoiceLineItems SKUs match —
// `doc.surface` is intentionally ignored (Leland 2026-06-02). The matched
// line items' `total_sqft` sum is the job's square footage for that type,
// compared against the installer's capacity_<field>_once/twice. Furniture and
// Step SKUs feed the per-type furniture/step sub-caps, mirroring carpet.
// Some SKUs appear under multiple types — that is expected/correct (Brandon).

// - - - main (square-footage) SKUs per type
export const JOB_VINYLPLANK_SKULIST = [503270, 6768460, 6768461, 1060634];
export const JOB_SHEETVINYL_SKULIST = [1097475, 1097474, 163973];
export const JOB_TILE_SKULIST = [104728, 104730, 104734];
export const JOB_BACKSPLASH_SKULIST = [
  1063132, 1063133, 1063134, 475356, 475358, 475359,
];
export const JOB_FLOATING_SKULIST = [
  1060635, 894131, 6768454, 6768458, 1243934, 503246, 503247, 503248, 712505,
  724006, 724007,
];
export const JOB_GLUEDOWN_SKULIST = [188208, 712504, 1051453, 163978];
export const JOB_NAILDOWN_SKULIST = [188204, 712217];

// - - - per-type Furniture/Day SKUs
export const JOB_VINYLPLANK_FURN_SKULIST = [503273, 900017, 1101363, 343409];
export const JOB_SHEETVINYL_FURN_SKULIST = [503273, 900017, 1101363, 343409];
export const JOB_TILE_FURN_SKULIST = [503276, 900008, 1101360, 343402];
export const JOB_BACKSPLASH_FURN_SKULIST = [7547029, 475368];
export const JOB_FLOATING_FURN_SKULIST = [
  503252, 503266, 900022, 900036, 1101362, 1101361, 343405, 343412,
];
export const JOB_GLUEDOWN_FURN_SKULIST = [
  503252, 503266, 900022, 900036, 1101362, 1101361, 343405, 343412,
];
export const JOB_NAILDOWN_FURN_SKULIST = [
  503252, 503266, 900022, 900036, 1101362, 1101361, 343405, 343412,
];

// - - - per-type Step/Day SKUs (some types have none)
export const JOB_VINYLPLANK_STEP_SKULIST = [1237223, 7395568, 7395569, 7395570];
export const JOB_SHEETVINYL_STEP_SKULIST = [];
export const JOB_TILE_STEP_SKULIST = [];
export const JOB_BACKSPLASH_STEP_SKULIST = [];
export const JOB_FLOATING_STEP_SKULIST = [
  1237229, 1237224, 7498983, 7395571, 7498982, 1237383, 7395572, 1237230,
];
export const JOB_GLUEDOWN_STEP_SKULIST = [7498983, 7498982, 7395572, 7395571];
export const JOB_NAILDOWN_STEP_SKULIST = [7498983, 7498982, 7395572, 7395571];

// - - - structured config the #173 capacity engine iterates. `capacityField`
// is the Candidates field prefix: capacity_<field>_once / _twice (+
// _once_furniture / _twice_furniture / _once_steps / _twice_steps).
export const HARD_SURFACE_CAPACITY_TYPES = [
  {
    type: "VinylPlank",
    capacityField: "vinyl",
    mainSkus: JOB_VINYLPLANK_SKULIST,
    furnitureSkus: JOB_VINYLPLANK_FURN_SKULIST,
    stepSkus: JOB_VINYLPLANK_STEP_SKULIST,
  },
  {
    type: "VinylSheet",
    capacityField: "vinylSheet",
    mainSkus: JOB_SHEETVINYL_SKULIST,
    furnitureSkus: JOB_SHEETVINYL_FURN_SKULIST,
    stepSkus: JOB_SHEETVINYL_STEP_SKULIST,
  },
  {
    type: "Tile",
    capacityField: "tile",
    mainSkus: JOB_TILE_SKULIST,
    furnitureSkus: JOB_TILE_FURN_SKULIST,
    stepSkus: JOB_TILE_STEP_SKULIST,
  },
  {
    type: "Backsplash",
    capacityField: "backsplash",
    mainSkus: JOB_BACKSPLASH_SKULIST,
    furnitureSkus: JOB_BACKSPLASH_FURN_SKULIST,
    stepSkus: JOB_BACKSPLASH_STEP_SKULIST,
  },
  {
    type: "Floating",
    capacityField: "floating",
    mainSkus: JOB_FLOATING_SKULIST,
    furnitureSkus: JOB_FLOATING_FURN_SKULIST,
    stepSkus: JOB_FLOATING_STEP_SKULIST,
  },
  {
    type: "GlueDown",
    capacityField: "glueDown",
    mainSkus: JOB_GLUEDOWN_SKULIST,
    furnitureSkus: JOB_GLUEDOWN_FURN_SKULIST,
    stepSkus: JOB_GLUEDOWN_STEP_SKULIST,
  },
  {
    type: "NailDown",
    capacityField: "nailDown",
    mainSkus: JOB_NAILDOWN_SKULIST,
    furnitureSkus: JOB_NAILDOWN_FURN_SKULIST,
    stepSkus: JOB_NAILDOWN_STEP_SKULIST,
  },
];

// ------------------

export const COLUMN_DAY_ARRAY = [
  "Mon",
  "Tue",
  "Wed",
  "Thr",
  "Fri",
  "Sat",
  "Sun",
];

// ------------------

export const RESCHEDULE_REASON_OPTIONS = [
  "Customer Reschedule",
  "Provider Reschedule",
  "Product Damaged",
  "Job Site Obstruction",
  "Product Delay",
];

// ------------------

export const JOB_INFO_CHECKBOXES = [
  { name: "Flags", field: "flags", selected: true },
  { name: "Customer Last Name", field: "lastName", selected: true },
  { name: "Qty", field: "qty", selected: true },
  { name: "Store #", field: "storeNo", selected: true },
  { name: "Arrival", field: "encodedArrival", selected: true },
  { name: "Classification", field: "classification", selected: true },
  { name: "Invoice #", field: "invoiceNo", selected: false },
  { name: "Project #", field: "projectNo", selected: false },
  { name: "Status", field: "statusCode", selected: false },
];

// ------------------

export const DOCUMENT_ATTRIBUTES_TEMPLATE = {
  companyCode: "",
  location: "",
  _kf_payrollNo: "",
  date: "",
  id: "",
  invoiceNo: 0,
  encodedLargeTitle: "",
  encodedSmallTitle: "",
  locked: false,
  distant: false,
  expedite: false,
};
